// Centralized Finnish UI strings. Kept flat and plain to stay easy to diff
// and to leave a clean path for i18n (react-intl / i18next) in a future phase
// without touching every component.

export const S = {
  appTitle: "Microsoft AI — What's New",
  appSubtitle:
    'Foundry, AI, Copilot, Modern Work, Cowork, Security & Partner | 12 lähdettä | viimeiset 6 kk',
  company: 'Clarito',
  greeting: 'Hei',

  liveLabel: 'Dataverse LIVE',
  loading: 'Ladataan...',
  loadingDataverse: 'Ladataan Dataversesta...',
  tryAgain: 'Yritä uudelleen',
  errorEmptyResponse: 'Flow palautti tyhjän vastauksen',

  viewCategories: '📊 Kategoriat',
  viewPriority: '🔥 Priorisoitu',
  viewRoadmap: '🗓 Roadmap',
  viewCommunity: '✍️ Yhteisö',
  searchPlaceholder: 'Hae päivityksiä...',

  sort: {
    label: 'Järjestä:',
    newest: 'Uusin ensin',
    upcoming: 'Tulossa',
  },

  stats: {
    total: 'Yhteensä',
    critical: 'Kriittistä',
    high: 'Korkea',
    actions: 'Toimenpidettä',
    ga: 'GA',
    deprecation: 'Poistuvat',
  },

  roadmap: {
    title: 'Roadmap',
    more: (n: number) => `+${n} lisää`,
    empty: 'Ei päivityksiä tässä aikaikkunassa',
    beyondLabel: 'Myöhemmin',
    legend: {
      ga: 'GA',
      deprecation: 'Poistuu',
      preview: 'Preview',
      other: 'Muu',
    },
  },

  typePills: {
    launched: 'Launched',
    inPreview: 'In Preview',
    inDevelopment: 'In Development',
    rollingOut: 'Rolling Out',
    retirement: 'Retirement',
    announcement: 'Announcement',
    update: 'Update',
  },

  card: {
    actionNeeded: '⚡ Toimenpide',
    actionLong: '⚡ Vaatii toimenpidettä',
    noAction: 'Ei toimenpiteitä',
    read: 'Lue Microsoftin sivuilta →',
    close: 'Sulje',
    category: 'Kategoria',
    impact: 'Vaikutus',
    type: 'Tyyppi',
    publishedAt: 'Julkaistu',
    source: 'Lähde',
    action: 'Toimenpide',
    // Expanded card: AI analysis box heading and the audience grid cell.
    analysisHeading: 'Mitä tämä tarkoittaa organisaatiolle',
    audience: 'Koskee',
    audienceLabels: {
      end_users: 'Loppukäyttäjät',
      admins: 'IT-ylläpito',
      developers: 'Kehittäjät',
    },
    // "Päivitetty" marker: the daily fetch detected a content change recently.
    changed: (date: string) => `Päivitetty ${date}`,
    changedTitle: (changed: string, published: string) =>
      `Sisältö päivittynyt ${changed}. Alun perin julkaistu ${published}.`,
  },

  emptyPanel: 'Ei päivityksiä valitulla suodattimella',
  emptyCategory: 'Ei päivityksiä tässä kategoriassa',
  emptyBookmarks: 'Ei kirjanmerkkejä',

  bookmark: {
    add: 'Lisää kirjanmerkki',
    remove: 'Poista kirjanmerkki',
    filter: '⭐ Kirjanmerkit',
  },

  mute: {
    add: 'Hiljennä (merkitse luetuksi)',
    remove: 'Poista hiljennys',
    showAll: 'Näytä kaikki',
    showAllTitle: (n: number) =>
      `Näytä myös hiljennetyt päivitykset (${n} hiljennetty)`,
  },

  // Yhteisö tab: posts from hand-picked independent bloggers.
  community: {
    title: 'Yhteisö',
    intro: 'Valittujen riippumattomien asiantuntijoiden blogikirjoitukset, tiivistetty tekoälyllä.',
    empty:
      'Täällä ei ole vielä kirjoituksia. Yhteisön blogit näkyvät tässä, kun ne on otettu käyttöön.',
    emptyFiltered: 'Ei kirjoituksia valituilla suodattimilla',
    emptyBookmarks: 'Ei yhteisön kirjanmerkkejä',
    author: 'Kirjoittaja',
    allAuthors: 'Kaikki kirjoittajat',
    topic: 'Aihe',
    postType: 'Tyyppi',
    all: 'Kaikki',
    bookmarksOnly: 'Vain kirjanmerkit',
    count: (n: number) => `${n} kirjoitusta`,
  },

  dataSources: {
    link: 'Tietolähteet',
    title: 'Tietolähteet',
    intro:
      'Päivitykset kootaan päivittäin seuraavista Microsoftin julkisista lähteistä ja tiivistetään tekoälyllä.',
    colSource: 'Lähde',
    colCategory: 'Kategoria',
    colCount: 'Päivityksiä (180 pv)',
    colNewest: 'Uusin',
    colLastRun: 'Viimeisin haku',
    colStatus: 'Tila',
    colNewItems: 'Uusia',
    disabled: 'pois käytöstä',
    // Section headings and the per-section toggle for disabled rows.
    sectionOfficial: 'Viralliset',
    sectionCommunity: 'Yhteisö',
    communityIntro: 'Valittujen riippumattomien bloggaajien kirjoitukset (Yhteisö-välilehti).',
    communityEmpty: 'Ei yhteisölähteitä.',
    colAuthor: 'Kirjoittaja',
    showDisabled: (n: number) => `Näytä myös pois käytöstä (${n})`,
    loadFailed: 'Lähdetietoja ei saatu ladattua.',
    // Header dot: a failed last run or a stale feed (see STALE_AFTER_DAYS).
    warning: 'Jossain tietolähteessä on ongelma',
    staleOld: (days: number) => `Ei uusia uutisia ${days} päivään`,
    staleEmpty: (days: number) => `Ei uutisia ${days} päivään`,
    other: 'Muu',
    none: '–',
    close: 'Sulje',
  },

  showAll: (n: number) => `Näytä kaikki ${n} →`,
  allCategory: (n: number) => `Kaikki ${n}`,
  footerPowered: 'Powered by Power Platform + AI Builder',
  connecting: 'Ei yhteydessä',
  dashboardPopulatesWhen: 'Ladataan päivityksiä Dataversesta...',
} as const;
